import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { API_BASE_URL } from '../../config/api';
import { useDisplayPolling } from '../../hooks/useDisplayPolling';
import { DisplayPollingService } from '../../services/DisplayPollingService';

/**
 * DisplayViewer Component - Simplified Dynamic Notes Approach with Global Polling Manager
 * 
 * Displays notes individually by fetching one at a time from the API
 * Includes rolling cache for network resilience and QR code immediate feedback
 * For unregistered displays (no displayId), shows QR code for registration
 * Uses global polling service to eliminate rapid-fire API calls
 */

// Types for display viewing
interface Note {
  _id: string;
  note: string;
  contributorAliasName?: string;
  location_city?: string;
  location_state?: string;
  createdAt: string;
  heart_count?: number;
  moderation_quality_score?: number;
}

interface DisplayConfig {
  name: string;
  customName?: string;
  visual?: {
    colorScheme?: string;
    showQrCode?: boolean;
    qrCodePlacement?: string;
    headerText?: string;
    footerText?: string;
  };
  content?: {
    displayDuration?: number; // in seconds
  };
}

// API response types
interface DisplayViewerProps {
  displayId?: string;
  authToken?: string;
  testMode?: boolean;
  testNotes?: Note[];
}

// Type for note stream API response
interface NoteStreamResponse {
  success: boolean;
  data: {
    note: Note;
  };
}

// Type for display authentication response
interface DisplayAuthResponse {
  success: boolean;
  data: {
    display_id: string;
    name: string;
    customName?: string;
    configuration: any;
  };
}

// Environment variables with defaults
const NOTE_DISPLAY_DURATION = parseInt(process.env.REACT_APP_NOTE_DISPLAY_DURATION || '60', 10); // seconds
const API_CALL_ADVANCE = parseInt(process.env.REACT_APP_API_CALL_ADVANCE || '20', 10); // seconds
const CACHE_SIZE = parseInt(process.env.REACT_APP_CACHE_SIZE || '20', 10); // number of notes

const DEFAULT_TRANSITION_DURATION = 1000; // milliseconds

// Generate a unique client ID for unregistered displays
const generateClientId = (): string => {
  return 'client_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now().toString(36);
};

const DisplayViewer: React.FC<DisplayViewerProps> = ({
  displayId: propDisplayId,
  authToken: propAuthToken,
  testMode = false,
  testNotes = []
}) => {
  // Get displayId from route parameters if not provided as prop
  const { displayId: routeDisplayId, token: routeAuthToken } = useParams<{ displayId?: string; token?: string }>();
  
  // Resolve displayId and authToken synchronously to avoid re-render loops
  const resolveDisplayContext = (): { displayId?: string; authToken?: string } => {
    const urlDisplayId = propDisplayId || routeDisplayId;
    const urlAuthToken = propAuthToken || routeAuthToken;
    
    // Simplified: URL parameters take priority, no token regeneration handling
    if (urlDisplayId && urlAuthToken) {
      console.log('[DisplayViewer] Using URL parameters for display context');
      return {
        displayId: urlDisplayId,
        authToken: urlAuthToken
      };
    }
    
    // Check localStorage for registered display info as fallback
    try {
      const storedDisplayInfo = localStorage.getItem(`display_${urlDisplayId}`);
      if (storedDisplayInfo) {
        const parsedInfo = JSON.parse(storedDisplayInfo);
        console.log('[DisplayViewer] Using localStorage display info as fallback');
        
        return {
          displayId: parsedInfo.displayId || urlDisplayId,
          authToken: parsedInfo.authToken
        };
      }
    } catch (error) {
      console.error('[DisplayViewer] Error parsing localStorage display info:', error);
      // Clear corrupted localStorage data
      if (urlDisplayId) {
        localStorage.removeItem(`display_${urlDisplayId}`);
      }
    }
    
    // Return URL parameters if available
    return {
      displayId: urlDisplayId,
      authToken: urlAuthToken
    };
  };
  
  // Get resolved display context
  const { displayId, authToken } = resolveDisplayContext();
  
  // Use the new display polling hook
  const { status, startRegistrationPolling, startActivationPolling, stopPolling } = useDisplayPolling();
  
  // Simplified state management
  const [currentNote, setCurrentNote] = useState<Note | null>(null);
  const [noteCache, setNoteCache] = useState<Note[]>([]);
  const [displayConfig, setDisplayConfig] = useState<DisplayConfig | null>(null);
  const [isOnline, setIsOnline] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [transitioning, setTransitioning] = useState<boolean>(false);
  
  // Generate client ID only once when component mounts for unregistered displays
  const [clientId] = useState<string>(() => {
    // Only generate if no displayId is provided (unregistered display)
    if (displayId) {
      console.log('[DisplayViewer] Display has displayId, not generating clientId');
      return '';
    }
    
    // Check if we have a registered display stored in localStorage
    const storedDisplayInfo = localStorage.getItem('registered_display_info');
    if (storedDisplayInfo) {
      try {
        const displayInfo = JSON.parse(storedDisplayInfo);
        
        // Simple validation: check if stored info has the required fields
        if (displayInfo.registration_id && displayInfo.static_auth_token) {
          console.log('[DisplayViewer] Found stored display info, will validate during initialization');
          // Don't redirect here - let the normal flow handle it
        } else {
          console.log('[DisplayViewer] Stored display is incomplete, clearing localStorage');
          localStorage.removeItem('registered_display_info');
        }
      } catch (error) {
        console.error('[DisplayViewer] Error parsing stored display info:', error);
        localStorage.removeItem('registered_display_info');
      }
    }
    
    // Try to get existing clientId from localStorage first
    const storedClientId = localStorage.getItem('display_client_id');
    if (storedClientId) {
      console.log('[DisplayViewer] Using stored clientId:', storedClientId);
      return storedClientId;
    }
    
    // Generate new clientId and store it
    const newClientId = generateClientId();
    localStorage.setItem('display_client_id', newClientId);
    console.log('[DisplayViewer] Generated new clientId:', newClientId);
    return newClientId;
  });
  
  // Refs for timers
  const displayTimerRef = useRef<NodeJS.Timeout | null>(null);
  const fetchTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Registration state for unregistered displays
  const [registrationData, setRegistrationData] = useState<any>(null);
  const [awaitingActivation, setAwaitingActivation] = useState<boolean>(false);
  const [displayDeactivated, setDisplayDeactivated] = useState<boolean>(false);
  const [lastSuccessfulFetch, setLastSuccessfulFetch] = useState<number>(0);

  /**
   * Add note to rolling cache (FIFO - newest first, oldest drops off)
   */
  const addToCache = (note: Note) => {
    setNoteCache(prev => {
      // Check if note already exists in cache to avoid duplicates
      const exists = prev.some(cachedNote => cachedNote._id === note._id);
      if (exists) return prev;
      
      // Add to front, slice to maintain cache size
      const updated = [note, ...prev].slice(0, CACHE_SIZE);
      console.log(`Added note to cache. Cache size: ${updated.length}/${CACHE_SIZE}`);
      return updated;
    });
  };

  /**
   * Get next note from cache (random selection)
   */
  const getNextCachedNote = (): Note | null => {
    if (noteCache.length === 0) return null;
    
    // Random selection from cache
    const randomIndex = Math.floor(Math.random() * noteCache.length);
    const note = noteCache[randomIndex];
    console.log(`Using cached note (${randomIndex + 1}/${noteCache.length}):`, note._id);
    return note;
  };

  /**
   * Fetch a single note from the API
   */
  const fetchSingleNote = useCallback(async (priorityDisplayId?: string): Promise<Note | null> => {
    try {
      setError(null);
      
      // Build URL with optional displayId for QR prioritization
      let url = `${API_BASE_URL}/display/notes/stream`;
      if (priorityDisplayId) {
        url += `?displayId=${priorityDisplayId}`;
      }
      
      const headers: Record<string, string> = {};
      
      // Add authentication header based on display type
      if (authToken) {
        headers['x-display-auth-token'] = authToken;
      } else if (clientId) {
        headers['x-client-id'] = clientId;
      }
      
      console.log('Fetching note from:', url, 'with headers:', headers);
      
      const response = await axios.get<NoteStreamResponse>(url, { headers });
      const note = response.data.data.note;
      
      if (note) {
        console.log('✅ Fetched note:', note._id);
        addToCache(note);
        setIsOnline(true);
        setLastSuccessfulFetch(Date.now()); // Track successful fetch
        return note;
      } else {
        console.warn('⚠️ No note returned from API');
        return null;
      }
      
    } catch (err: any) {
      console.error('❌ Error fetching note:', err);
      
      // Handle specific error cases for display deactivation/deletion
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        // Display deactivated - stop timers and trigger polling service to handle transition
        console.log('🛑 Display deactivated, stopping timers');
        console.log('🔍 Debug - Deactivation context:', { displayId, authToken });
        
        // Stop timers directly to avoid circular dependency
        if (displayTimerRef.current) {
          clearInterval(displayTimerRef.current);
          displayTimerRef.current = null;
        }
        if (fetchTimerRef.current) {
          clearInterval(fetchTimerRef.current);
          fetchTimerRef.current = null;
        }
        
        // Pass display context to the polling service for proper reactivation handling
        DisplayPollingService.handleDisplayDeactivation(displayId, authToken);
        
        setDisplayDeactivated(true);
        setLastSuccessfulFetch(0); // Reset successful fetch tracking
      } else if (err?.response?.status === 404) {
        // Display deleted - stop timers and trigger polling service to handle transition
        console.log('🛑 Display deleted, stopping timers');
        // Stop timers directly to avoid circular dependency
        if (displayTimerRef.current) {
          clearInterval(displayTimerRef.current);
          displayTimerRef.current = null;
        }
        if (fetchTimerRef.current) {
          clearInterval(fetchTimerRef.current);
          fetchTimerRef.current = null;
        }
        DisplayPollingService.handleDisplayDeletion();
      }
      
      setIsOnline(false);
      
      // Try to use cached note as fallback
      const cachedNote = getNextCachedNote();
      if (cachedNote) {
        console.log('📦 Using cached note as fallback');
        setError('Network error - showing cached content');
        return cachedNote;
      } else {
        setError('Unable to load notes');
        return null;
      }
    }
  }, [authToken, clientId, displayId, addToCache, getNextCachedNote]);

  /**
   * Track that a note was displayed (fire-and-forget)
   */
  const trackNoteDisplayed = useCallback(async (noteId: string) => {
    try {
      const headers: Record<string, string> = {};
      
      if (authToken) {
        headers['x-display-auth-token'] = authToken;
      } else if (clientId) {
        headers['x-client-id'] = clientId;
      }
      
      await axios.post(`${API_BASE_URL}/display/notes/${noteId}/displayed`, {}, { headers });
      console.log('📊 Tracked note display:', noteId);
    } catch (err) {
      console.warn('⚠️ Failed to track note display:', err);
      // Non-critical error, don't throw
    }
  }, [authToken, clientId]);

  /**
   * Start display and fetch timers
   */
  const startTimers = useCallback(() => {
    console.log('⏰ Starting display timers');
    
    // Stop any existing timers first
    stopTimers();
    
    // Display timer: show current note for configured duration
    displayTimerRef.current = setInterval(async () => {
      console.log('🔄 Display timer tick - fetching next note');
      
      setTransitioning(true);
      
      try {
        const nextNote = await fetchSingleNote(displayId);
        if (nextNote) {
          setCurrentNote(nextNote);
          // Track that this note was displayed
          trackNoteDisplayed(nextNote._id);
        }
      } catch (err) {
        console.error('Error in display timer:', err);
      } finally {
        // Reset transition after a short delay
        setTimeout(() => setTransitioning(false), DEFAULT_TRANSITION_DURATION);
      }
    }, NOTE_DISPLAY_DURATION * 1000);

    // Fetch timer: get next note in advance (for better cache)
    fetchTimerRef.current = setInterval(async () => {
      console.log('📥 Fetch timer tick - pre-fetching note for cache');
      try {
        await fetchSingleNote(displayId);
      } catch (err) {
        console.error('Error in fetch timer:', err);
      }
    }, (NOTE_DISPLAY_DURATION - API_CALL_ADVANCE) * 1000);
  }, [displayId, fetchSingleNote, trackNoteDisplayed]);

  /**
   * Stop all timers
   */
  const stopTimers = () => {
    if (displayTimerRef.current) {
      clearInterval(displayTimerRef.current);
      displayTimerRef.current = null;
    }
    if (fetchTimerRef.current) {
      clearInterval(fetchTimerRef.current);
      fetchTimerRef.current = null;
    }
  };

  // Handle polling status changes
  useEffect(() => {
    // Only start registration polling if:
    // 1. No displayId (unregistered display)
    // 2. Have clientId
    // 3. Currently unregistered
    // 4. Not already polling (to prevent multiple instances)
    if (!displayId && clientId && status.state === 'unregistered' && !status.isPolling) {
      console.log('[DisplayViewer] Starting registration polling');
      startRegistrationPolling(clientId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayId, clientId, status.state, startRegistrationPolling]); // Intentionally excluding status.isPolling to prevent polling loops

  useEffect(() => {
    if (status.state === 'registered' && status.registrationData) {
      console.log('[DisplayViewer] Display registered, updating state');
      setRegistrationData(status.registrationData);
      setAwaitingActivation(true);
      
      // Start activation polling
      if (clientId) {
        startActivationPolling(clientId, status.registrationData);
      }
    }
  }, [status.state, status.registrationData, clientId, startActivationPolling]);

  useEffect(() => {
    if (status.state === 'active' && status.registrationData) {
      console.log('[DisplayViewer] Display activated, redirecting');
      
      // Store the active display information
      const displayInfo = {
        registration_id: status.registrationData.registration_id,
        static_auth_token: status.registrationData.static_auth_token,
        name: status.registrationData.name,
        customName: status.registrationData.customName,
        status: 'active',
        storedAt: Date.now()
      };
      localStorage.setItem('registered_display_info', JSON.stringify(displayInfo));
      
      // Clear the stored clientId
      localStorage.removeItem('display_client_id');
      
      // Redirect to active display
      const redirectUrl = `/display/view/${status.registrationData.registration_id}/${status.registrationData.static_auth_token}`;
      window.location.href = redirectUrl;
    }
  }, [status.state, status.registrationData]);

  useEffect(() => {
    if (status.error) {
      setError(status.error);
    }
  }, [status.error]);

  // Monitor polling status for display reactivation
  useEffect(() => {
    // If display was deactivated but polling service indicates it's active again
    if (displayDeactivated && status.state === 'active' && status.registrationData) {
      // Check if this reactivation is for the current display
      const statusDisplayId = status.registrationData.displayId || status.registrationData.registration_id;
      const statusAuthToken = status.registrationData.authToken || status.registrationData.static_auth_token;
      
      if (statusDisplayId === displayId && statusAuthToken === authToken) {
        console.log('🔄 Display reactivated, restarting timers and clearing deactivated state');
        setDisplayDeactivated(false);
        startTimers();
      }
    }
  }, [displayDeactivated, status, displayId, authToken, startTimers]);

  // Additional check: If display is marked as deactivated but we're successfully fetching notes,
  // then the display is actually active and we should clear the deactivated state
  useEffect(() => {
    if (displayDeactivated && lastSuccessfulFetch > 0) {
      // Only clear deactivated state if we've successfully fetched notes AFTER deactivation
      console.log('🔄 Display appears to be active (successfully fetching notes after deactivation), clearing deactivated state');
      setDisplayDeactivated(false);
      // Stop any unnecessary polling
      DisplayPollingService.stopPolling();
      // Restart timers since display is working again
      startTimers();
    }
  }, [displayDeactivated, lastSuccessfulFetch, startTimers]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
      stopTimers();
    };
  }, [stopPolling]);

  // Initialize display for active displays
  useEffect(() => {
    const initializeDisplay = async () => {
      // If no displayId provided, check if we have stored display info to use
      if (!displayId) {
        const storedDisplayInfo = localStorage.getItem('registered_display_info');
        if (storedDisplayInfo) {
          try {
            const displayInfo = JSON.parse(storedDisplayInfo);
            if (displayInfo.registration_id && displayInfo.static_auth_token && displayInfo.status === 'active') {
              // Validate that the stored display still exists
              console.log('Found stored display info, validating if display still exists...');
              try {
                const testResponse = await axios.get<DisplayAuthResponse>(`${API_BASE_URL}/displays/token/${displayInfo.static_auth_token}`);
                if (testResponse.data.success) {
                  // Display exists, redirect to it
                  const redirectUrl = `/display/view/${displayInfo.registration_id}/${displayInfo.static_auth_token}`;
                  console.log('Stored display is valid, redirecting to:', redirectUrl);
                  window.location.href = redirectUrl;
                  return;
                } else {
                  console.log('Stored display validation failed, clearing localStorage');
                  localStorage.removeItem('registered_display_info');
                }
              } catch (validationErr: any) {
                console.log('Stored display no longer exists, clearing localStorage');
                localStorage.removeItem('registered_display_info');
                localStorage.removeItem('display_client_id');
              }
            }
          } catch (error) {
            console.error('Error parsing stored display info:', error);
            localStorage.removeItem('registered_display_info');
          }
        }
        setLoading(false);
        return;
      }
      
      if (!authToken) {
        setLoading(false);
        return;
      }

      try {
        console.log('🚀 Initializing active display with ID:', displayId);
        
        // Authenticate display and get configuration
        const authResponse = await axios.get<DisplayAuthResponse>(`${API_BASE_URL}/displays/token/${authToken}`);
        
        if (authResponse.data.success) {
          const displayData = authResponse.data.data;
          console.log('✅ Display authenticated:', displayData);
          
          setDisplayConfig({
            name: displayData.name,
            customName: displayData.customName,
            ...displayData.configuration
          });
          
          // Fetch initial note
          const initialNote = await fetchSingleNote(displayId);
          if (initialNote) {
            setCurrentNote(initialNote);
            trackNoteDisplayed(initialNote._id);
          }
          
          // Start display timers
          startTimers();
          
        } else {
          setError('Display authentication failed');
        }
      } catch (err: any) {
        console.error('❌ Display initialization failed:', err);
        
        if (err?.response?.status === 404) {
          console.log('Display not found (404), clearing stored display info');
          // Clear localStorage since this display no longer exists
          localStorage.removeItem('registered_display_info');
          localStorage.removeItem('display_client_id');
          setError('Display not found - it may have been deleted');
        } else if (err?.response?.status === 403) {
          setError('Display is not active');
        } else {
          setError('Failed to initialize display');
        }
      } finally {
        setLoading(false);
      }
    };

    initializeDisplay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayId, authToken]);

  /**
   * Get contributor name with fallback
   */
  const getContributorName = (note: Note) => {
    return note.contributorAliasName || 'Anonymous';
  };

  /**
   * Get color scheme based on configuration
   */
  const getColorScheme = () => {
    const scheme = displayConfig?.visual?.colorScheme || 'default';
    
    switch (scheme) {
      case 'dark':
        return {
          background: '#1a1a1a',
          text: '#ffffff',
          accent: '#4a9eff',
          muted: '#888888'
        };
      case 'light':
        return {
          background: '#ffffff',
          text: '#333333',
          accent: '#007bff',
          muted: '#666666'
        };
      case 'warm':
        return {
          background: '#fef7e0',
          text: '#8b4513',
          accent: '#d2691e',
          muted: '#a0522d'
        };
      default:
        return {
          background: '#f8f9fa',
          text: '#212529',
          accent: '#007bff',
          muted: '#6c757d'
        };
    }
  };

  const colors = getColorScheme();
  
  if (loading) {
    return (
      <div className="display-viewer-loading" style={{ background: colors.background, color: colors.text }}>
        <div className="text-center p-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading display...</span>
          </div>
          <h3 className="mt-3">Loading display...</h3>
          {error && (
            <p className="mt-2 text-muted">
              {error}
            </p>
          )}
        </div>
      </div>
    );
  }
  
  // Show deactivated display message
  if (displayDeactivated) {
    return (
      <div className="display-viewer-deactivated" style={{ 
        background: colors.background, 
        color: colors.text,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '2rem',
        textAlign: 'center',
        position: 'relative'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: '#dc3545',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '2rem',
          color: 'white',
          fontSize: '2rem'
        }}>
          ⏸
        </div>
        
        <h1 style={{ 
          fontSize: '3rem', 
          marginBottom: '1rem',
          fontWeight: 'bold',
          color: '#dc3545'
        }}>
          Display Deactivated
        </h1>
        
        <div style={{ 
          fontSize: '1.5rem', 
          marginBottom: '2rem',
          lineHeight: '1.6',
          maxWidth: '600px'
        }}>
          <p style={{ marginBottom: '1rem' }}>
            This display has been deactivated and is no longer showing content.
          </p>
          <p style={{ marginBottom: '1rem' }}>
            To resume showing content, please reactivate this display in your account dashboard.
          </p>
        </div>
        
        <div style={{ 
          fontSize: '1rem',
          opacity: 0.7,
          maxWidth: '500px',
          lineHeight: '1.5',
          backgroundColor: 'rgba(220, 53, 69, 0.1)',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid rgba(220, 53, 69, 0.3)'
        }}>
          <p style={{ margin: '0 0 0.5rem 0', fontWeight: 'bold', color: '#dc3545' }}>
            ⚠️ Display Status: Inactive
          </p>
          <p style={{ margin: '0' }}>
            This page will automatically refresh when the display is reactivated. 
            You can also manually reactivate it in your account dashboard.
          </p>
        </div>
      </div>
    );
  }
  
  // Show registration QR code for unregistered displays
  if (!displayId && clientId) {
    // If display is registered but awaiting activation
    if (awaitingActivation) {
      return (
        <div className="display-viewer-activation" style={{ 
          background: colors.background, 
          color: colors.text,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '2rem',
          textAlign: 'center'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: '#28a745',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '2rem',
            color: 'white',
            fontSize: '2rem'
          }}>
            ✓
          </div>
          
          <h1 style={{ 
            fontSize: '3rem', 
            marginBottom: '1rem',
            fontWeight: 'bold',
            color: '#28a745'
          }}>
            Display Registered Successfully!
          </h1>
          
          <div style={{ 
            fontSize: '1.5rem', 
            marginBottom: '2rem',
            lineHeight: '1.6',
            maxWidth: '600px'
          }}>
            <p style={{ marginBottom: '1rem' }}>
              Your display <strong>{registrationData?.name || 'has been registered'}</strong> and is ready to be activated.
            </p>
            <p style={{ marginBottom: '1rem' }}>
              To complete the setup and start showing content:
            </p>
            <ol style={{ 
              textAlign: 'left', 
              fontSize: '1.25rem',
              lineHeight: '1.8',
              marginLeft: '2rem'
            }}>
              <li className="mb-2">
                Go to your <strong>My Displays</strong> page in your account
              </li>
              <li className="mb-2">
                Find this display: <strong>{registrationData?.name}</strong>
              </li>
              <li>
                Click the <strong>"Activate"</strong> button to start showing content
              </li>
            </ol>
          </div>
          
          <div style={{ 
            fontSize: '1.2rem',
            opacity: 0.8,
            marginBottom: '1rem'
          }}>
            <strong>Display ID:</strong> {clientId}
          </div>
          
          <div style={{ 
            fontSize: '1rem',
            opacity: 0.7,
            maxWidth: '500px',
            lineHeight: '1.5',
            backgroundColor: 'rgba(40, 167, 69, 0.1)',
            padding: '1rem',
            borderRadius: '8px',
            border: '1px solid rgba(40, 167, 69, 0.3)'
          }}>
            <p style={{ margin: '0 0 0.5rem 0', fontWeight: 'bold', color: '#28a745' }}>
              🔄 Waiting for Activation
            </p>
            <p style={{ margin: '0' }}>
              This page will automatically update once you activate the display in your account. 
              No need to refresh manually!
            </p>
          </div>
        </div>
      );
    }
    
    const registrationUrl = `${window.location.origin}/my-displays/register/${clientId}`;
    
    return (
      <div className="display-viewer-registration" style={{ 
        background: colors.background, 
        color: colors.text,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '2rem',
        textAlign: 'center',
        position: 'relative'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: '#28a745',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '2rem',
          color: 'white',
          fontSize: '2rem'
        }}>
          ✓
        </div>
        
        <h1 style={{ 
          fontSize: '3rem', 
          marginBottom: '1rem',
          fontWeight: 'bold'
        }}>
          Display Setup Required
        </h1>
        
        <div style={{ 
          fontSize: '1.5rem', 
          marginBottom: '3rem',
          lineHeight: '1.6',
          maxWidth: '600px'
        }}>
          <p style={{ marginBottom: '1rem' }}>
            To connect this display to your account, please:
          </p>
          <ol style={{ 
            textAlign: 'left', 
            fontSize: '1.25rem',
            lineHeight: '1.8'
          }}>
            <li>Scan the QR code below with your phone</li>
            <li>Log in to your account</li>
            <li>Fill out the display registration form</li>
            <li>This page will automatically update when complete</li>
          </ol>
        </div>
        
        <div style={{ 
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '10px',
          marginBottom: '2rem',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
        }}>
          <QRCodeSVG 
            value={registrationUrl}
            size={300}
            level="M"
            includeMargin={true}
          />
        </div>
        
        <div style={{ 
          fontSize: '1.2rem',
          opacity: 0.8,
          marginBottom: '1rem'
        }}>
          <strong>Display ID:</strong> {clientId}
        </div>
        
        <div style={{ 
          fontSize: '1rem',
          opacity: 0.7,
          maxWidth: '500px',
          lineHeight: '1.5'
        }}>
          <p style={{ marginBottom: '0.5rem' }}>
            <strong>Can't scan the QR code?</strong>
          </p>
          <p>
            Manually navigate to: <br />
            <code style={{ 
              backgroundColor: 'rgba(255,255,255,0.1)', 
              padding: '4px 8px', 
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}>
              {registrationUrl}
            </code>
          </p>
        </div>
      </div>
    );
  }
  
  // Show error state with retry information
  if (error) {
    return (
      <div className="display-viewer-error" style={{ 
        background: colors.background, 
        color: colors.text,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '2rem',
        textAlign: 'center'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: '#dc3545',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '2rem',
          color: 'white',
          fontSize: '2rem'
        }}>
          ⚠
        </div>
        
        <h1 style={{ 
          fontSize: '2.5rem', 
          marginBottom: '1rem',
          fontWeight: 'bold',
          color: '#dc3545'
        }}>
          Display Error
        </h1>
        
        <div style={{ 
          fontSize: '1.25rem', 
          marginBottom: '2rem',
          lineHeight: '1.6',
          maxWidth: '600px',
          backgroundColor: '#f8f9fa',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          {error}
        </div>
        
        <div style={{ 
          fontSize: '1rem',
          opacity: 0.7,
          maxWidth: '500px',
          lineHeight: '1.5'
        }}>
          <p>If this error persists, please:</p>
          <ul style={{ textAlign: 'left', marginTop: '1rem' }}>
            <li>Check your network connection</li>
            <li>Verify the display token is valid</li>
            <li>Contact support if the issue continues</li>
          </ul>
        </div>
      </div>
    );
  }
  
  if (!currentNote) {
    return (
      <div className="display-viewer-empty" style={{ background: colors.background, color: colors.text }}>
        <div className="text-center p-5">
          <h3>No Notes Available</h3>
          <p>This display has no notes to show.</p>
          {displayConfig && (
            <p>Display: {displayConfig.customName || displayConfig.name}</p>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="display-viewer" style={{ 
      background: colors.background, 
      color: colors.text,
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div className="display-viewer-header" style={{ 
        padding: '1rem',
        borderBottom: `1px solid ${colors.accent}`,
        backgroundColor: colors.accent,
        color: 'white',
        textAlign: 'center'
      }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem' }}>
          {displayConfig?.customName || displayConfig?.name || 'Digital Display'}
        </h1>
        {displayConfig?.visual?.headerText && (
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '1rem', opacity: 0.9 }}>
            {displayConfig.visual.headerText}
          </p>
        )}
      </div>

      {/* Main Content Area */}
      <div className="display-viewer-content" style={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '2rem',
        position: 'relative',
        opacity: transitioning ? 0.3 : 1,
        transition: `opacity ${DEFAULT_TRANSITION_DURATION}ms ease-in-out`
      }}>
        {/* Note Content */}
        <div className="note-content" style={{ 
          textAlign: 'center',
          maxWidth: '800px',
          width: '100%'
        }}>
          <blockquote style={{ 
            fontSize: '2.5rem',
            lineHeight: '1.4',
            margin: '0 0 2rem 0',
            fontWeight: '300',
            fontStyle: 'italic'
          }}>
            "{currentNote.note}"
          </blockquote>
          
          <div className="note-attribution" style={{ 
            fontSize: '1.5rem',
            color: colors.muted,
            marginBottom: '1rem'
          }}>
            — {getContributorName(currentNote)}
            {currentNote.location_city && currentNote.location_state && (
              <span> from {currentNote.location_city}, {currentNote.location_state}</span>
            )}
          </div>
          
          {currentNote.heart_count && currentNote.heart_count > 0 && (
            <div className="note-hearts" style={{ 
              fontSize: '1.25rem',
              color: colors.accent,
              marginBottom: '1rem'
            }}>
              ❤️ {currentNote.heart_count} {currentNote.heart_count === 1 ? 'heart' : 'hearts'}
            </div>
          )}
        </div>

        {/* QR Code (if enabled) */}
        {displayConfig?.visual?.showQrCode && displayId && (
          <div className="qr-code-container" style={{ 
            position: 'absolute',
            bottom: '2rem',
            right: '2rem',
            backgroundColor: 'white',
            padding: '10px',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <QRCodeSVG 
              value={`${window.location.origin}/notes/${currentNote._id}`}
              size={120}
              level="M"
              includeMargin={true}
            />
            <div style={{ 
              textAlign: 'center', 
              fontSize: '0.8rem', 
              color: '#666', 
              marginTop: '5px' 
            }}>
              Scan to share
            </div>
          </div>
        )}

        {/* Network Status Indicator */}
        {!isOnline && (
          <div className="network-status" style={{ 
            position: 'absolute',
            top: '1rem',
            left: '1rem',
            backgroundColor: '#ffc107',
            color: '#212529',
            padding: '0.5rem 1rem',
            borderRadius: '20px',
            fontSize: '0.9rem',
            fontWeight: 'bold'
          }}>
            📦 Offline Mode
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="display-viewer-footer" style={{ 
        padding: '1rem',
        borderTop: `1px solid ${colors.muted}`,
        textAlign: 'center',
        fontSize: '0.9rem',
        color: colors.muted
      }}>
        {displayConfig?.visual?.footerText || 'Powered by StampOutSuicide.org'}
        {error && (
          <span style={{ marginLeft: '1rem', color: '#ffc107' }}>
            ⚠️ {error}
          </span>
        )}
      </div>
    </div>
  );
};

export default DisplayViewer;